import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Shield, Key, User, AlertCircle, RefreshCw, Lock } from 'lucide-react'

const AdminLogin = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    captchaId: '',
    captchaValue: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [captchaEnabled, setCaptchaEnabled] = useState(false)
  const [captchaValue, setCaptchaValue] = useState('')
  const [captchaId, setCaptchaId] = useState('')
  const [remainingAttempts, setRemainingAttempts] = useState(null)
  const [locked, setLocked] = useState(false)
  const navigate = useNavigate()

  // 检查是否需要验证码
  useEffect(() => {
    checkCaptchaStatus()
  }, [])

  const checkCaptchaStatus = async () => {
    try {
      const resp = await fetch('/api/auth/captcha')
      const data = await resp.json()
      if (data.success) {
        // 后台返回验证码信息，说明验证码功能已就绪
        // 首次登录不需要验证码，错误后才需要
      }
    } catch (e) {
      // 静默处理
    }
  }

  const requestCaptcha = async () => {
    try {
      const resp = await fetch('/api/auth/captcha')
      const data = await resp.json()
      if (data.success) {
        setCaptchaId(data.data.captchaId)
        setCaptchaEnabled(true)
      }
    } catch (e) {
      console.error('获取验证码失败:', e)
    }
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
    setError('')
    setLocked(false)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setLocked(false)

    try {
      const payload = { 
        username: formData.username, 
        password: formData.password 
      }
      
      if (captchaEnabled) {
        payload.captchaId = captchaId
        payload.captchaValue = formData.captchaValue
      }

      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      const data = await response.json()

      if (data.success) {
        localStorage.setItem('token', data.data.token)
        localStorage.setItem('user', JSON.stringify(data.data.user))
        navigate('/admin/dashboard')
      } else {
        setError(data.message || '登录失败')
        
        // 处理剩余尝试次数
        if (data.remainingAttempts !== undefined) {
          setRemainingAttempts(data.remainingAttempts)
          if (data.remainingAttempts <= 2) {
            requestCaptcha()
          }
        }
        
        // 检查是否被锁定
        if (response.status === 429 || data.message?.includes('锁定')) {
          setLocked(true)
        }

        // 清空验证码
        setFormData(prev => ({ ...prev, captchaValue: '' }))
      }
    } catch (error) {
      console.error('登录错误:', error)
      setError('网络错误，请稍后重试')
    } finally {
      setLoading(false)
    }
  }

  // 刷新验证码
  const refreshCaptcha = async () => {
    try {
      const resp = await fetch('/api/auth/captcha')
      const data = await resp.json()
      if (data.success) {
        setCaptchaId(data.data.captchaId)
        setCaptchaValue(data.data.captchaValue)
        setFormData(prev => ({ ...prev, captchaValue: '' }))
      }
    } catch (e) {
      console.error('刷新验证码失败:', e)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-blue-600 rounded-full flex items-center justify-center mb-4">
            <Shield className="text-white" size={32} />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">
            管理员登录
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            无锡市摩恒科技有限公司后台管理系统
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg sm:rounded-lg sm:px-10">
          <form className="space-y-5" onSubmit={handleSubmit}>
            {locked && (
              <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-md flex items-start space-x-2">
                <Lock size={18} className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-sm">账号已临时锁定</p>
                  <p className="text-xs mt-1">{error || '请稍后再试'}</p>
                </div>
              </div>
            )}
            
            {error && !locked && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md flex items-start space-x-2">
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm">{error}</p>
                  {remainingAttempts !== null && remainingAttempts > 0 && (
                    <p className="text-xs mt-1 text-red-500">
                      剩余 {remainingAttempts} 次尝试机会
                    </p>
                  )}
                </div>
              </div>
            )}

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                用户名
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User size={16} className="text-gray-400" />
                </div>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  disabled={locked}
                  value={formData.username}
                  onChange={handleChange}
                  className="appearance-none block w-full pl-10 px-3 py-2.5 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100"
                  placeholder="请输入用户名"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                密码
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Key size={16} className="text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  disabled={locked}
                  value={formData.password}
                  onChange={handleChange}
                  className="appearance-none block w-full pl-10 px-3 py-2.5 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm disabled:bg-gray-100"
                  placeholder="请输入密码"
                />
              </div>
            </div>

            {captchaEnabled && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  验证码
                </label>
                <div className="flex space-x-3">
                  <input
                    name="captchaValue"
                    type="text"
                    required
                    value={formData.captchaValue}
                    onChange={handleChange}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="请输入验证码"
                    maxLength={4}
                    style={{ letterSpacing: '0.5em', textAlign: 'center', fontSize: '1.2em' }}
                  />
                  <button
                    type="button"
                    onClick={refreshCaptcha}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-600 flex items-center"
                    title="刷新验证码"
                  >
                    <RefreshCw size={16} />
                  </button>
                </div>
                {captchaValue && (
                  <p className="text-xs text-gray-500 mt-1">
                    验证码（开发环境可见）: <span className="font-mono font-bold text-gray-900">{captchaValue}</span>
                  </p>
                )}
              </div>
            )}

            <div className="pt-2">
              <button
                type="submit"
                disabled={loading || locked}
                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <span className="flex items-center">
                    <RefreshCw className="animate-spin mr-2" size={16} />
                    登录中...
                  </span>
                ) : (
                  '登录'
                )}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-white text-gray-500">
                  安全登录 · 数据加密
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminLogin
